import { action, computed, makeObservable, observable } from 'mobx';
import i18next from 'i18next';
import {
	ActionCoreData,
	ActionType,
	NarrowActionableWpTimeWindowData,
	NarrowActionableWpTimeWindowDataActionMetadata
} from '@bringg/types';

import { ActionsRepo } from './internal';
import ActionBaseStore from './action-base-store';
import { ActionFamilyType, ClientNarrowActionableWpTimeWindowAction, RoundToMinutes } from '../utils/types';

export type { RoundToMinutes };

export const MAX_BUFFER_IN_MINUTES = 180; // 3 HOURS

// BRNGG-55987: FE-owned key; BRNGG-55969 (rule-service executor) consumes the identical string.
export const ASYMMETRIC_BUFFERS_NARROW_TIME_WINDOW_FF = 'asymmetric_buffers_narrow_time_window';

// Newly-added buffers start empty (0, invalid) so the user types a real value - matches the epic's
// own mockup flow (narrow_time_window_final_flow.html, step 2: "Start input appears empty ... not
// yet saveable until value > 0 entered"), not the 45/135 example values from the rough .tsx scaffold.
const DEFAULT_NEW_BUFFER_IN_MINUTES = 0;

class NarrowActionableWpTimeWindowActionStore extends ActionBaseStore {
	type = ActionFamilyType.NARROW_ACTIONABLE_WP_TIME_WINDOW;
	action_type = ActionType.NARROW_ACTIONABLE_WP_TIME_WINDOW;

	bufferInMinutes: NarrowActionableWpTimeWindowData['buffer_in_minutes'];

	startBufferInMinutes?: number;
	endBufferInMinutes?: number;
	roundToMinutes?: RoundToMinutes;
	// Set by the component from the FF (mirrors realtime-driver-location-webhook-action-store's
	// globalWebhookInfraEnabled). Defaults false so mappedToServer is byte-for-byte unchanged
	// unless the component explicitly opts in.
	useAsymmetricShape = false;

	// True only when start/endBufferInMinutes came from a real FF-on save (mapper's
	// hasNewShapeFields), not from client-side legacy-mirroring for display. Gates whether
	// mappedToServer's FF-off branch is allowed to preserve them (BRNGG-55969 council finding -
	// mirrored values must not be persisted as if the user configured them).
	private readonly hydratedWithNewShape: boolean;

	private readonly initialBufferInMinutes: NarrowActionableWpTimeWindowData['buffer_in_minutes'];

	constructor(actionToInit: ClientNarrowActionableWpTimeWindowAction, actionsRepo: ActionsRepo) {
		super(actionsRepo);
		this.hydratedWithNewShape = actionToInit.hasNewShapeFields ?? false;
		this.initialBufferInMinutes = actionToInit.bufferInMinutes ?? 0;
		makeObservable(this, {
			bufferInMinutes: observable,
			startBufferInMinutes: observable,
			endBufferInMinutes: observable,
			roundToMinutes: observable,
			useAsymmetricShape: observable,
			setBufferInMinutes: action,
			setStartBufferInMinutes: action,
			setEndBufferInMinutes: action,
			setRoundToMinutes: action,
			setUseAsymmetricShape: action,
			addStartBuffer: action,
			addEndBuffer: action,
			removeStartBuffer: action,
			removeEndBuffer: action,
			hasAnyNewBuffer: computed,
			hasAllBuffers: computed,
			isNewUiValid: computed,
			title: computed
		});

		this.bufferInMinutes = actionToInit.bufferInMinutes ?? 0;
		this.startBufferInMinutes = actionToInit.startBufferInMinutes;
		this.endBufferInMinutes = actionToInit.endBufferInMinutes;
		this.roundToMinutes = actionToInit.roundToMinutes;
	}

	// buffer_in_minutes is required (non-optional, number | null) on the real type, so it's always
	// kept - only the three asymmetric fields are conditionally omitted when unset.
	private static readonly OPTIONAL_ASYMMETRIC_KEYS = [
		'start_buffer_minutes',
		'end_buffer_minutes',
		'round_to_minutes'
	] as const;

	get mappedToServer(): ActionCoreData {
		if (!this.useAsymmetricShape) {
			// FF-off can't newly SET the asymmetric fields (that render path stays untouched), but
			// must not silently ERASE them if they're already hydrated from a real earlier FF-on
			// save - a workflow save is a full-replace, not a patch, so dropping them here would
			// permanently delete a merchant's configured data on the next unrelated save.
			// Only preserve when hydratedWithNewShape is true - otherwise startBufferInMinutes/
			// endBufferInMinutes are just the mapper's client-side legacy-mirroring for display,
			// and persisting them would fabricate asymmetric config the user never configured.
			// setBufferInMinutes already clears these to undefined the moment the user actually
			// edits the legacy buffer, so nothing further to gate on here.
			const legacyData: NarrowActionableWpTimeWindowData = {
				buffer_in_minutes: this.bufferInMinutes,
				...(this.hydratedWithNewShape
					? {
							start_buffer_minutes: this.startBufferInMinutes,
							end_buffer_minutes: this.endBufferInMinutes,
							round_to_minutes: this.roundToMinutes
					  }
					: {})
			};

			NarrowActionableWpTimeWindowActionStore.OPTIONAL_ASYMMETRIC_KEYS.forEach(key => {
				if (legacyData[key] == null) {
					delete legacyData[key];
				}
			});

			return {
				action_type: ActionType.NARROW_ACTIONABLE_WP_TIME_WINDOW,
				data: legacyData
			};
		}

		const data: NarrowActionableWpTimeWindowData = {
			buffer_in_minutes: null,
			start_buffer_minutes: this.startBufferInMinutes,
			end_buffer_minutes: this.endBufferInMinutes,
			round_to_minutes: this.roundToMinutes
		};

		NarrowActionableWpTimeWindowActionStore.OPTIONAL_ASYMMETRIC_KEYS.forEach(key => {
			if (data[key] == null) {
				delete data[key];
			}
		});

		return {
			action_type: ActionType.NARROW_ACTIONABLE_WP_TIME_WINDOW,
			data
		};
	}

	get isValid(): boolean {
		if (this.useAsymmetricShape) {
			return this.isNewUiValid;
		}

		if (this.bufferInMinutes === null) {
			return true;
		}

		if (this.bufferInMinutes >= 0 && this.bufferInMinutes <= MAX_BUFFER_IN_MINUTES) {
			return true;
		}

		return false;
	}

	setBufferInMinutes = (buffer: number | null): void => {
		this.bufferInMinutes = buffer;

		// The legacy editor is the only UI available while FF is off - an explicit edit here is
		// the user's real intent, so drop any previously-hydrated asymmetric config instead of
		// letting it silently win again once FF returns
		if (this.hydratedWithNewShape && buffer !== this.initialBufferInMinutes) {
			this.startBufferInMinutes = undefined;
			this.endBufferInMinutes = undefined;
			this.roundToMinutes = undefined;
		}
	};

	get hasAnyNewBuffer(): boolean {
		return this.startBufferInMinutes !== undefined || this.endBufferInMinutes !== undefined;
	}

	// Drives whether the "+ add ..." link row still has anything left to show.
	get hasAllBuffers(): boolean {
		return this.startBufferInMinutes !== undefined && this.endBufferInMinutes !== undefined;
	}

	// Exposed so the component can highlight the exact offending input (per mockup: the empty/invalid
	// field itself turns red), not just show one generic message for the whole action.
	static isBufferValueValid(value?: number): boolean {
		return value === undefined || (Number.isInteger(value) && value > 0 && value <= MAX_BUFFER_IN_MINUTES);
	}

	get isNewUiValid(): boolean {
		if (!this.hasAnyNewBuffer) {
			return false;
		}

		return (
			NarrowActionableWpTimeWindowActionStore.isBufferValueValid(this.startBufferInMinutes) &&
			NarrowActionableWpTimeWindowActionStore.isBufferValueValid(this.endBufferInMinutes)
		);
	}

	setStartBufferInMinutes = (value: number): void => {
		this.startBufferInMinutes = value;
	};

	setEndBufferInMinutes = (value: number): void => {
		this.endBufferInMinutes = value;
	};

	setRoundToMinutes = (value: RoundToMinutes | undefined): void => {
		this.roundToMinutes = value;
	};

	setUseAsymmetricShape = (value: boolean): void => {
		this.useAsymmetricShape = value;
	};

	// Rounding is NOT auto-enabled here - it defaults to "not rounded" (roundToMinutes stays
	// undefined) until the user explicitly picks "Rounded" from the toggle, which is where the
	// 15-default actually belongs (component's rounding-menu onMenuClick handler).
	addStartBuffer = (): void => {
		this.startBufferInMinutes = DEFAULT_NEW_BUFFER_IN_MINUTES;
	};

	addEndBuffer = (): void => {
		this.endBufferInMinutes = DEFAULT_NEW_BUFFER_IN_MINUTES;
	};

	// Deletes the value (does not zero it) so the underlying data survives a toggle off/on.
	removeStartBuffer = (): void => {
		this.startBufferInMinutes = undefined;
		if (this.endBufferInMinutes === undefined) {
			this.roundToMinutes = undefined;
		}
	};

	removeEndBuffer = (): void => {
		this.endBufferInMinutes = undefined;
		if (this.startBufferInMinutes === undefined) {
			this.roundToMinutes = undefined;
		}
	};

	get title(): string {
		const titleKey = (this.actionData as NarrowActionableWpTimeWindowDataActionMetadata).translation_string;
		return i18next.t(titleKey);
	}

	get icon(): string {
		return (this.actionData as NarrowActionableWpTimeWindowDataActionMetadata).icon || '';
	}
}

export default NarrowActionableWpTimeWindowActionStore;
